import type { Lang } from '../i18n';

// Dates are stored in UTC and shown in the group's head-office time zone (CLAUDE.md "Dates").
// One format everywhere (D102): 01 Nov 2026, and 01 Nov 2026, 14:05 with a 24-hour clock.
// English months are always three letters (Intl's en-GB writes "Sept").

const MONTHS_EN = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function parts(d: Date, timeZone: string, time: boolean) {
  const p = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(time ? { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' as const } : {}),
    timeZone,
  }).formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) => p.find((x) => x.type === type)?.value ?? '';
  return {
    day: get('day'),
    month: Number(get('month')),
    year: get('year'),
    hour: get('hour'),
    minute: get('minute'),
  };
}

function arabic(d: Date, timeZone: string, time: boolean): string {
  return new Intl.DateTimeFormat('ar-SA-u-ca-gregory-nu-latn', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(time ? { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' as const } : {}),
    timeZone,
  }).format(d);
}

export function formatDate(iso: string, lang: Lang, timeZone = 'Asia/Riyadh'): string {
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return '';
  // Date-only values (needed-by) are calendar dates, not instants.
  const zone = iso.length === 10 ? 'UTC' : timeZone;
  if (lang === 'ar') return arabic(d, zone, false);
  const x = parts(d, zone, false);
  return `${x.day} ${MONTHS_EN[x.month - 1] ?? ''} ${x.year}`;
}

export function formatDateTime(iso: string, lang: Lang, timeZone = 'Asia/Riyadh'): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  if (lang === 'ar') return arabic(d, timeZone, true);
  const x = parts(d, timeZone, true);
  return `${x.day} ${MONTHS_EN[x.month - 1] ?? ''} ${x.year}, ${x.hour}:${x.minute}`;
}

/** The weekday too, for the line under a date picker: "Thu, 20 Nov 2026". */
export function formatDateLong(ymd: string, lang: Lang): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '';
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  const day = new Intl.DateTimeFormat(lang === 'ar' ? 'ar-SA-u-ca-gregory-nu-latn' : 'en-GB', {
    weekday: 'short',
    timeZone: 'UTC',
  }).format(d);
  return `${day}, ${formatDate(ymd, lang)}`;
}
